/**
 * Filter options with a count beside each one, returned by the listing
 * endpoints alongside `data` and `pagination`.
 *
 * Two things about the payload are worth knowing before rendering it:
 *
 * 1. **A facet's counts ignore that facet's own selection.** Selecting
 *    "free" does not drop the "paid" count to zero — otherwise the user
 *    could never switch back without clearing the filter first. Every
 *    *other* active filter does narrow it. Render the counts exactly as
 *    sent; recomputing them client-side produces wrong numbers.
 *
 * 2. **Boolean groups label themselves in English** — `"Yes"` / `"No"`,
 *    not «بله» / «خیر», despite what the integration guide claims. Any UI
 *    that renders a boolean group as a list has to supply its own Persian
 *    labels. `category` and `mini_app` labels are correctly localised.
 *
 * The whole object is absent when the search cluster is unreachable, and on
 * an API with no search backend deployed at all. That means "no counts right
 * now", never "no filter options exist" — the filter UI has to stand without
 * it. Range filters (price, discount, rating, dates) are never faceted.
 */
export interface FacetBucket<TValue = string | number | boolean> {
  /** Send this straight back as the filter value. */
  value: TValue;
  /** Display text. Localised for `category`/`mini_app`, English for booleans. */
  label: string;
  /** How many courses this option would give. */
  count: number;
}

export interface SearchFacets {
  category?: FacetBucket<number>[];
  level?: FacetBucket<number>[];
  language?: FacetBucket<string>[];
  target_audience?: FacetBucket<number>[];
  educator?: FacetBucket<number>[];
  /** Omitted on a mini-app-scoped listing. */
  mini_app?: FacetBucket<string>[];
  is_free?: FacetBucket<boolean>[];
  has_certificate?: FacetBucket<boolean>[];
  has_quiz?: FacetBucket<boolean>[];
  has_online_class?: FacetBucket<boolean>[];
}

/** The count for one side of a boolean facet, or undefined when unavailable. */
export function booleanFacetCount(
  buckets: FacetBucket<boolean>[] | undefined,
  value: boolean,
): number | undefined {
  return buckets?.find((bucket) => bucket.value === value)?.count;
}

/**
 * Category counts keyed by id, covering both tree levels — the facet carries a
 * bucket per parent *and* per child.
 */
export function categoryFacetCounts(
  buckets: FacetBucket<number>[] | undefined,
): Map<number, number> | null {
  if (!buckets) return null;
  return new Map(buckets.map((bucket) => [bucket.value, bucket.count]));
}

/**
 * Every bucket either object carries, keyed by group. Labels come from whichever
 * side has them; counts are not meaningful here and are left at the newer value.
 *
 * Used to accumulate the widest set of options seen for one search, because the
 * API drops a bucket the moment nothing matches it rather than returning it with
 * a zero count.
 */
export function unionFacets(
  previous: SearchFacets | null,
  next: SearchFacets | null,
): SearchFacets {
  const keys = new Set([
    ...Object.keys(previous ?? {}),
    ...Object.keys(next ?? {}),
  ]) as Set<keyof SearchFacets>;

  const merged: Record<string, FacetBucket[]> = {};

  for (const key of keys) {
    const byValue = new Map<string, FacetBucket>();
    for (const bucket of (previous?.[key] ?? []) as FacetBucket[]) {
      byValue.set(String(bucket.value), bucket);
    }
    for (const bucket of (next?.[key] ?? []) as FacetBucket[]) {
      byValue.set(String(bucket.value), bucket);
    }
    merged[key] = [...byValue.values()];
  }

  return merged as SearchFacets;
}

/**
 * The accumulated options, each carrying its count from the live response — or
 * zero when the live response dropped it.
 *
 * This is what keeps the filter UI still: refining a search narrows the counts
 * instead of deleting rows, so an option the user is about to tick doesn't
 * vanish mid-reach, and one they already ticked never becomes unreachable.
 * Ordered by count so the useful options stay at the top and the dead ones sink.
 */
export function withLiveCounts(
  seen: SearchFacets | null,
  live: SearchFacets | null,
): SearchFacets | null {
  if (!seen) return live;

  const result: Record<string, FacetBucket[]> = {};

  for (const [key, buckets] of Object.entries(seen)) {
    const liveCounts = new Map(
      ((live?.[key as keyof SearchFacets] ?? []) as FacetBucket[]).map(
        (bucket) => [String(bucket.value), bucket.count],
      ),
    );
    result[key] = (buckets as FacetBucket[])
      .map((bucket) => ({
        ...bucket,
        count: liveCounts.get(String(bucket.value)) ?? 0,
      }))
      .sort((a, b) => b.count - a.count);
  }

  return result as SearchFacets;
}
